import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { useThemeStore } from '@/state/themeStore';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, Activity, Sun, Moon, Menu } from 'lucide-react';
import StudioSwitcher from '@/components/shell/StudioSwitcher';
import PersonaSwitcher from '@/components/shell/PersonaSwitcher';
import HelpLauncher from '@/components/shell/HelpLauncher';

function formatSimClock(iso: string) {
  const d = new Date(iso);
  const opts: Intl.DateTimeFormatOptions = {
    timeZone: 'Australia/Melbourne',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  return new Intl.DateTimeFormat('en-AU', opts).format(d);
}

export default function TopBar() {
  const navigate = useNavigate();
  const clockIso = useSimulationStore((s) => s.clockIso);
  const muted = useUIStore((s) => s.muted);
  const railCollapsed = useUIStore((s) => s.railCollapsed);
  const toggleRail = useUIStore((s) => s.toggleRail);
  const toggleActivityFeed = useUIStore((s) => s.toggleActivityFeed);
  const setMuted = useUIStore((s) => s.setMuted);
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  return (
    <header className="sticky top-0 z-30 bg-ink-deep border-b border-gold-dim/20 h-16 px-6 flex items-center gap-6">
      <button
        type="button"
        onClick={toggleRail}
        className="btn-quiet px-2"
        data-testid="rail-toggle"
        aria-label={railCollapsed ? 'Expand navigation' : 'Collapse navigation'}
        aria-expanded={!railCollapsed}
        title={railCollapsed ? 'Show menu' : 'Hide menu'}
      >
        <Menu size={18} />
      </button>

      <button
        type="button"
        onClick={() => navigate('/')}
        className="flex items-center"
        data-testid="brand-mark"
        aria-label="Tattoo Vault — home"
      >
        <span className="font-empera text-xl text-gold-bright uppercase tracking-widest whitespace-nowrap shrink-0">
          Tattoo Vault
        </span>
      </button>

      <div
        className="flex items-center gap-3 px-3 py-1.5 rounded-full border border-gold-dim/30"
        data-testid="sim-clock"
        title="Studio time"
      >
        <span className="font-mono text-sm text-gold-bright tabular-nums">
          {formatSimClock(clockIso)}
        </span>
      </div>

      <div className="flex-1" />

      <div className="flex items-center gap-3">
        <StudioSwitcher />
        <HelpLauncher />
        <button
          type="button"
          onClick={toggleTheme}
          className="btn-quiet"
          data-testid="theme-toggle"
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
        >
          {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
        </button>
        <button
          type="button"
          onClick={() => setMuted(!muted)}
          className="btn-quiet"
          data-testid="audio-toggle"
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <BellOff size={14} /> : <Bell size={14} />}
        </button>
        <button
          type="button"
          onClick={toggleActivityFeed}
          className="btn-quiet"
          data-testid="activity-feed-toggle"
        >
          <Activity size={14} /> Feed
        </button>
        <PersonaSwitcher />
      </div>
    </header>
  );
}
